import { useMemo } from "react";
import Chart from "react-apexcharts";
import styled from "styled-components";

// #region SCOPED CSS
const StyledChart = styled.div`
    width: 100%;
    min-width: 0;
`;
// #endregion

// #region CONSTANTS
const DEFAULT_COLORS = ["#7c868b", "#0060cc", "#00bb00"];

const formatCompactAxisValue = value => {
    const absoluteValue = Math.abs(value);
    if (absoluteValue >= 1000000) return `${value / 1000000}M`;
    if (absoluteValue >= 1000) return `${value / 1000}K`;
    return `${value}`;
};

const formatTooltipValue = value => new Intl.NumberFormat("de-DE").format(value);
// #endregion

/**
 * Standard grouped vertical-column chart.
 *
 * @param {Object} props
 * @param {string[]} props.categories
 * @param {{ name: string, data: number[] }[]} props.series
 * @param {string[]} [props.colors]
 * @param {number} [props.height]
 * @param {number} [props.max]
 * @param {number} [props.tickAmount]
 * @param {(value: number) => string} [props.valueFormatter]
 * @param {string} [props.ariaLabel]
 */
export default function ChartGroupedColumns({
    categories = [],
    series = [],
    colors = DEFAULT_COLORS,
    height = 220,
    max,
    tickAmount = 4,
    valueFormatter = formatTooltipValue,
    ariaLabel = "Grouped column chart"
}) {
    const options = useMemo(
        () => ({
            chart: {
                type: "bar",
                background: "transparent",
                toolbar: { show: false },
                animations: { enabled: true },
                redrawOnWindowResize: true,
                redrawOnParentResize: true
            },
            colors,
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: "42%",
                    borderRadius: 0
                }
            },
            dataLabels: { enabled: false },
            legend: { show: false },
            stroke: { show: false },
            fill: { opacity: 1 },
            grid: {
                show: true,
                borderColor: "#3c485a",
                xaxis: { lines: { show: false } },
                yaxis: { lines: { show: true } },
                padding: { top: 0, right: 8, bottom: 0, left: 4 }
            },
            xaxis: {
                categories,
                axisBorder: { show: true, color: "#3c485a" },
                axisTicks: { show: false },
                labels: {
                    trim: false,
                    style: {
                        colors: categories.map(() => "#d1d1d1"),
                        fontSize: "10px"
                    }
                },
                tooltip: { enabled: false }
            },
            yaxis: {
                min: 0,
                max,
                tickAmount,
                labels: {
                    formatter: formatCompactAxisValue,
                    style: {
                        colors: ["#d1d1d1"],
                        fontSize: "10px"
                    }
                }
            },
            tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
                theme: "dark",
                fillSeriesColor: false,
                y: {
                    formatter: valueFormatter
                }
            },
            noData: {
                text: "No chart data",
                style: { color: "#7c868b" }
            }
        }),
        [categories, colors, max, tickAmount, valueFormatter]
    );

    return (
        <StyledChart role="img" aria-label={ariaLabel}>
            <Chart type="bar" height={height} width="100%" options={options} series={series} />
        </StyledChart>
    );
}

